import {emotionStyled, fontSizeAtom} from '@packages/utilities/styled';
import {tokens} from '../../themes';

/**
 * Used to improve layout rendering for text truncation (label).
 *
 * @see https://webflow.atlassian.net/browse/DS-598
 */

const SWITCH_WIDTH = 40;

export const switchDensities = {
  track: {
    comfortable: {
      height: tokens.ui.switch.track.comfortable.height,
      width: tokens.ui.switch.track.comfortable.width,
    },
    compact: {
      height: tokens.ui.switch.track.compact.height,
      width: tokens.ui.switch.track.compact.width,
    },
  },
  knob: {
    comfortable: {
      height: tokens.ui.switch.knob.comfortable.height,
      width: tokens.ui.switch.knob.comfortable.width,
    },
    compact: {
      height: tokens.ui.switch.knob.compact.height,
      width: tokens.ui.switch.knob.compact.width,
    },
  },
};

export const ElementWrapperView = emotionStyled('div')(
  {
    position: 'relative',
    // used to prevent overflow when in a flex container. This is especially important when the label text needs be constrained by the parent elements. We don't want it to get too greedy.
    minWidth: 0,
  },
  {
    componentName: 'SwitchInput',
    variants: {
      hasNoLabel: {
        true: {
          // if there is no label, we want to allow the wrapper to grow to the full size of the Switch itself to make sure the entire thing is clickable.
          minWidth: 'unset',
        },
      },
    },
  }
);

export const SwitchTrackView = emotionStyled('div')(
  {
    backgroundColor: '$background3',
    borderColor: '$border3',
    borderRadius: 99999,
    borderStyle: 'solid',
    borderWidth: tokens.controls.borderWidth,
    height: switchDensities.track.comfortable.height,
    width: switchDensities.track.comfortable.width,
    padding: 0,
    pointerEvents: 'none',
    [`${ElementWrapperView}:hover &`]: {
      background: '$actionSecondaryBackgroundHover',
      borderColor: 'transparent',
    },
    '*:focus-visible + &': {
      outline: `1px solid ${tokens.colors.blueBorder}`,
      outlineOffset: '1px',
    },
  },
  {
    componentName: 'SwitchTrack',
    variants: {
      isChecked: {
        true: {
          backgroundColor: '$actionPrimaryBackground',
          borderColor: '$actionPrimaryBackground',
          [`${ElementWrapperView}:hover &`]: {
            background: '$actionPrimaryBackgroundHover',
          },
        },
      },
      isDisabled: {
        true: {
          backgroundColor: '$background3',
          [`${ElementWrapperView}:hover &`]: {
            backgroundColor: '$background3',
          },
        },
      },
      density: {
        comfortable: {
          height: switchDensities.track.comfortable.height,
          width: switchDensities.track.comfortable.width,
        },
        compact: {
          height: switchDensities.track.compact.height,
          width: switchDensities.track.compact.width,
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          isChecked: true,
          isDisabled: true,
        },
        css: {
          backgroundColor: '$actionPrimaryBackground',
          [`${ElementWrapperView}:hover &`]: {
            backgroundColor: '$actionPrimaryBackground',
          },
        },
      },
    ],
  }
);

export const SwitchKnobView = emotionStyled('div')(
  {
    backgroundColor: '$white',
    borderRadius: 9999,
    boxShadow: '$1',
    height: 16,
    margin: 1,
    transition: '120ms transform',
    width: 16,
  },
  {
    componentName: 'SwitchKnob',
    variants: {
      disabled: {
        true: {
          pointerEvents: 'none',
          boxShadow: 'none',
        },
      },
      isChecked: {
        true: {},
      },
      density: {
        comfortable: {
          height: switchDensities.knob.comfortable.height,
          width: switchDensities.knob.comfortable.width,
        },
        compact: {
          height: switchDensities.knob.compact.height,
          width: switchDensities.knob.compact.width,
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          isChecked: true,
          density: 'compact',
        },
        css: {
          transform: `translateX(8px)`,
        },
      },
      {
        conditions: {
          isChecked: true,
          density: 'comfortable',
        },
        css: {
          transform: `translateX(12px)`,
        },
      },
    ],
  }
);

export const SwitchElementView = emotionStyled('input')({
  appearance: 'none',
  bottom: 0,
  cursor: 'inherit',
  height: '100%',
  left: 0,
  margin: 0,
  opacity: 0,
  padding: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  width: '100%',
});

export const SwitchView = emotionStyled('label')(
  {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    color: '$textPrimary',
    cursor: 'pointer',
    display: 'inline-flex',
    margin: 0,
    maxWidth: '100%',
    minWidth: 0,
    padding: 0,
    position: 'relative',
  },
  {
    componentName: 'Switch',
    variants: {
      size: {
        // Existing size outside of sizeContext.
        small: {
          gap: '$2',
        },
        medium: {
          // All interactive content in Designer uses `cursor: default`
          cursor: 'default',
          gap: '$2',
        },
        'x-large': {
          gap: '$4',
        },
      },
      isDisabled: {
        true: {
          opacity: tokens.opacities.disabled,
          cursor: 'not-allowed',
        },
      },
    },
  }
);

export const SwitchLabelView = emotionStyled('span')(
  {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '$1',
    whiteSpace: 'nowrap',
    // Resets the height of the label.
    lineHeight: 1,
  },
  {
    componentName: 'SwitchLabel',
    composes: [fontSizeAtom],
    variants: {
      density: {
        comfortable: {
          maxWidth: `calc(100% - ${SWITCH_WIDTH}px)`,
        },
        compact: {
          // The compact density switch width is 25% smaller.
          maxWidth: `calc(100% - ${SWITCH_WIDTH * 0.75}px)`,
        },
      },
    },
  }
);
